/**
 * Resize handle specifically for OdDbBlockReference entities.
 *
 * ODA's transformBy() does not support non-uniform scaling for BlockReferences.
 * This handle uses setScaleFactors() and setPosition() directly instead.
 *
 * Performance optimization: Uses the ODA view.add() pattern from BaseHandle.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type {
  TransformContext,
  KeyPoints,
  Point3D,
  ResizeAxes,
} from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { deleteObj } from "../../core/delete";
import { openAs } from "../../core/oda";
import {
  BlockReferenceTransformCommand,
  blockRefStateChanged,
  type BlockRefState,
  type BlockRefStateParams,
} from "../../commands/BlockReferenceTransformCommand";
import { MeasurementLabels } from "../../overlay/MeasurementLabels";
import { applyDeltaScaleWithAnchorPoint } from "@/drawing/blocks/transform";

/**
 * Handle for resizing BlockReference entities (non-uniform scaling).
 * Uses setScaleFactors() and setPosition() instead of transformBy().
 */
export class BlockReferenceResizeHandle extends BaseHandle {
  readonly type = HandleType.Resize;
  readonly position: HandlePosition;

  /** The anchor point (opposite corner/edge) for resizing */
  private anchorPoint: Point3D;

  /** Axis constraints */
  private axes: ResizeAxes;

  /** Original state captured at start (for undo) */
  private originalState: BlockRefState | null = null;

  private measureLabels: MeasurementLabels | null = null;

  /** Previous scale values for incremental updates */
  private prevScaleX = 1;
  private prevScaleY = 1;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
    position: HandlePosition,
    anchorPosition: HandlePosition,
    axes: ResizeAxes = { x: true, y: true },
  ) {
    super(viewer, history, entityIds, keyPoints, position);

    this.position = position;
    this.axes = axes;

    // Set anchor to specified position
    this.anchorPoint = this.getPositionFromKeyPoints(anchorPosition);

    // Override draw options for resize handle
    const isEdge = !axes.x || !axes.y;
    this.drawOptions = {
      fillColor: isEdge
        ? TRANSFORM_THEME.handleFill
        : TRANSFORM_THEME.scaleHandle,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius:
        (isEdge ? HANDLE_SIZES.scale - 1 : HANDLE_SIZES.scale) *
        HANDLE_SIZES.handleScale,
      icon: "scale",
    };
  }

  /**
   * Start resize - capture original BlockReference state.
   */
  start(): void {
    super.start();
    // Reset scale tracking
    this.prevScaleX = 1;
    this.prevScaleY = 1;

    // Capture original state for the BlockReference
    if (this.entityIds.length > 0) {
      const entity = openAs(this.entityIds[0], this.module.OdDbEntity, 0);
      const blockRef = this.module.OdDbBlockReference.cast(entity);

      if (blockRef) {
        const scale = blockRef.scaleFactors();
        const pos = blockRef.position();

        this.originalState = {
          scaleFactors: [scale.sx, scale.sy, scale.sz],
          position: [pos.x, pos.y, pos.z],
        };

        deleteObj(scale);
        deleteObj(pos);
      }
      deleteObj(entity);
    }
  }

  /**
   * Update resize during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate distances from anchor to handle (original) and to cursor (current)
    const originalDistanceX = this.worldPosition.x - this.anchorPoint.x;
    const originalDistanceY = this.worldPosition.y - this.anchorPoint.y;

    const currentDistanceX = context.worldPoint.x - this.anchorPoint.x;
    const currentDistanceY = context.worldPoint.y - this.anchorPoint.y;

    // Calculate scale ratios
    let scaleX = 1;
    let scaleY = 1;

    if (this.axes.x && Math.abs(originalDistanceX) > 0.0001) {
      scaleX = currentDistanceX / originalDistanceX;
    }

    if (this.axes.y && Math.abs(originalDistanceY) > 0.0001) {
      scaleY = currentDistanceY / originalDistanceY;
    }

    // Clamp to reasonable values and prevent flipping
    scaleX = Math.max(0.1, Math.min(10, scaleX));
    scaleY = Math.max(0.1, Math.min(10, scaleY));

    // Apply scale using BlockReference-specific methods
    this.applyBlockReferenceScale(scaleX, scaleY);

    // Update view with throttling for better performance
    this.throttledUpdate();

    this.updateMeasurements(context, scaleX, scaleY);
  }

  private updateMeasurements(
    context: TransformContext,
    scaleX: number,
    scaleY: number,
  ): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    // Current world dimensions = original * |scale|
    const originalWidth = Math.abs(this.keyPoints.max.x - this.keyPoints.min.x);
    const originalHeight = Math.abs(
      this.keyPoints.max.y - this.keyPoints.min.y,
    );
    const currentWidth = originalWidth * Math.abs(scaleX);
    const currentHeight = originalHeight * Math.abs(scaleY);

    // Screen positions: anchor is fixed, cursor represents the moving edge/corner
    const anchorScreen = this.viewer.worldToScreen(this.anchorPoint);
    const cursorScreen = context.screenPoint;

    // Determine min/max screen coords for label positioning
    const sMinX = Math.min(anchorScreen.x, cursorScreen.x);
    const sMaxX = Math.max(anchorScreen.x, cursorScreen.x);
    const sMinY = Math.min(anchorScreen.y, cursorScreen.y);
    const sMaxY = Math.max(anchorScreen.y, cursorScreen.y);

    // Width guide line + label (only if X axis is active)
    if (this.axes.x) {
      this.measureLabels.setLine("width", sMinX, sMinY, sMaxX, sMinY);
      this.measureLabels.setLabel(
        "width",
        (sMinX + sMaxX) / 2,
        sMinY - 18,
        MeasurementLabels.formatDistance(currentWidth),
      );
    }

    // Height guide line + label (only if Y axis is active)
    if (this.axes.y) {
      this.measureLabels.setLine("height", sMaxX, sMinY, sMaxX, sMaxY);
      this.measureLabels.setLabel(
        "height",
        sMaxX + 18,
        (sMinY + sMaxY) / 2,
        MeasurementLabels.formatDistance(currentHeight),
      );
    }
  }

  /**
   * Apply scale to BlockReference using setScaleFactors and setPosition.
   * Uses ghost entity if available (for performance), otherwise original.
   */
  private applyBlockReferenceScale(scaleX: number, scaleY: number): void {
    // Compute delta scale from previous
    const deltaScaleX =
      this.prevScaleX !== 0 ? scaleX / this.prevScaleX : scaleX;
    const deltaScaleY =
      this.prevScaleY !== 0 ? scaleY / this.prevScaleY : scaleY;

    for (const data of this.transformData) {
      // Use ghost entity if available (fast path), otherwise fall back to original
      const entity =
        data.ghostEntity ?? openAs(data.entityId, this.module.OdDbEntity, 1);
      const isGhost = !!data.ghostEntity;
      const blockRef = this.module.OdDbBlockReference.cast(entity);

      if (blockRef) {
        applyDeltaScaleWithAnchorPoint(
          this.module,
          blockRef,
          this.anchorPoint,
          deltaScaleX,
          deltaScaleY,
        );
      }

      // PERFORMANCE: Only call recordGraphicsModified for non-ghost entities
      if (!isGhost) {
        entity.recordGraphicsModified(true);
        deleteObj(entity);
      }
    }

    // Update previous values
    this.prevScaleX = scaleX;
    this.prevScaleY = scaleY;
  }

  /**
   * End resize - execute BlockReference-specific command.
   */
  end(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;

    if (!this.isTransforming) return;

    this.isTransforming = false;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // Get final state from ghost (if using ghosts) or original
    let newState: BlockRefState | null = null;

    // Clean up ghosts and apply final state to originals
    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Get final state from ghost
        const ghostBlockRef = this.module.OdDbBlockReference.cast(
          data.ghostEntity,
        );
        if (ghostBlockRef && !newState) {
          const scale = ghostBlockRef.scaleFactors();
          const pos = ghostBlockRef.position();
          newState = {
            scaleFactors: [scale.sx, scale.sy, scale.sz],
            position: [pos.x, pos.y, pos.z],
          };
          deleteObj(scale);
          deleteObj(pos);
        }

        // Remove ghost from view
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;

        // Apply final state to original
        if (newState) {
          const originalEntity = openAs(
            data.entityId,
            this.module.OdDbEntity,
            1,
          );
          const originalBlockRef =
            this.module.OdDbBlockReference.cast(originalEntity);
          if (originalBlockRef) {
            const scale = new this.module.OdGeScale3d(
              newState.scaleFactors[0],
              newState.scaleFactors[1],
              newState.scaleFactors[2],
            );
            const pos = new this.module.OdGePoint3d(
              newState.position[0],
              newState.position[1],
              newState.position[2],
            );
            originalBlockRef.setScaleFactors(scale);
            originalBlockRef.setPosition(pos);
            deleteObj(scale);
            deleteObj(pos);
          }
          originalEntity.recordGraphicsModified(true);
          deleteObj(originalEntity);
        }
      } else {
        // Non-ghost path: get state from original
        const entity = openAs(data.entityId, this.module.OdDbEntity, 0);
        const blockRef = this.module.OdDbBlockReference.cast(entity);
        if (blockRef && !newState) {
          const scale = blockRef.scaleFactors();
          const pos = blockRef.position();
          newState = {
            scaleFactors: [scale.sx, scale.sy, scale.sz],
            position: [pos.x, pos.y, pos.z],
          };
          deleteObj(scale);
          deleteObj(pos);
        }
        deleteObj(entity);
      }
    }

    // Undo/redo replays absolute placements, not an invertible transform: a
    // BlockReference is scaled by writing setScaleFactors()/setPosition().
    const before = this.originalState;
    const after = newState;
    const params: BlockRefStateParams[] =
      before && after
        ? this.transformData.map((data) => ({
            handle: data.handle,
            before,
            after,
          }))
        : [];

    // Clear state
    this.transformData = [];
    this.originalState = null;

    // Execute command if there was actual change
    if (blockRefStateChanged(params)) {
      const command = new BlockReferenceTransformCommand(this.viewer, params);
      this.history.execute(command);
    }

    device?.invalidate();
    try {
      this.safeViewerUpdate("BlockReferenceResizeHandle.end", {
        entityHandles: entityHandlesForLog,
      });
    } finally {
      for (const ghost of ghostsToDelete) deleteObj(ghost);
    }
  }

  /**
   * Cancel resize - restore original BlockReference state.
   */
  cancel(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;

    if (!this.isTransforming) return;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // Clean up ghosts (originals were never modified, so no restore needed)
    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Remove ghost from view
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;
      } else {
        // Non-ghost path: restore original state
        if (this.originalState) {
          const entity = openAs(data.entityId, this.module.OdDbEntity, 1);
          const blockRef = this.module.OdDbBlockReference.cast(entity);

          if (blockRef) {
            const scale = new this.module.OdGeScale3d(
              this.originalState.scaleFactors[0],
              this.originalState.scaleFactors[1],
              this.originalState.scaleFactors[2],
            );
            const pos = new this.module.OdGePoint3d(
              this.originalState.position[0],
              this.originalState.position[1],
              this.originalState.position[2],
            );

            blockRef.setScaleFactors(scale);
            blockRef.setPosition(pos);

            deleteObj(scale);
            deleteObj(pos);
          }

          entity.recordGraphicsModified(true);
          deleteObj(entity);
        }
      }
    }

    // Reset state
    this.prevScaleX = 1;
    this.prevScaleY = 1;
    this.originalState = null;

    this.isTransforming = false;
    this.transformData = [];

    device?.invalidate();
    try {
      this.safeViewerUpdate("BlockReferenceResizeHandle.cancel", {
        entityHandles: entityHandlesForLog,
      });
    } finally {
      for (const ghost of ghostsToDelete) deleteObj(ghost);
    }
  }
}
